import React, { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  CloudUploadIcon,
  DropZoneFileIcon,
  CheckCircleFilledIcon,
  CloseIcon,
  SmallErrorCircleIcon,
} from "../../../icons";
import "./FileDropZone.scss";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024,
    sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function FileDropZone({
  id,
  accept,
  file,
  error,
  progress,
  onFileSelect,
  onClear,
  disabled = false,
  dropLabel = "Drag & drop your file here",
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (files) => {
      if (disabled || !files?.length) return;
      onFileSelect(files[0]);
    },
    [disabled, onFileSelect]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const openBrowse = (e) => {
    e.stopPropagation();
    if (!disabled) inputRef.current?.click();
  };

  const isUploading = progress !== undefined && progress >= 0 && progress < 100;
  const isSuccess = progress === 100;
  const isFailed = progress === -1;
  const hasFile = Boolean(file);

  let zoneClass = "fdz-zone";
  if (dragging) zoneClass += " fdz-zone--drag";
  if (hasFile && !error) zoneClass += " fdz-zone--filled";
  if (error) zoneClass += " fdz-zone--error";
  if (disabled) zoneClass += " fdz-zone--disabled";
  if (isSuccess && !error) zoneClass += " fdz-zone--success";

  return (
    <div className="fdz-wrap">
      {/* ── Drop zone ── */}
      <div
        className={zoneClass}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          className="fdz-input"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
        />

        {!hasFile ? (
          /* ── Empty / idle state ── */
          <div className="fdz-empty">
            <div className="fdz-cloud-icon">
              <CloudUploadIcon size={46} color="#4f46e5" />
            </div>
            <span className="fdz-drop-label">{dragging ? "Drop file here" : dropLabel}</span>
            <span className="fdz-or">or</span>
            <button
              type="button"
              className="fdz-browse-btn"
              onClick={openBrowse}
              disabled={disabled}
            >
              Browse Files
            </button>
          </div>
        ) : (
          /* ── File selected state ── */
          <div className="fdz-file-row">
            <div className="fdz-file-icon-wrap">
              {isSuccess ? <CheckCircleFilledIcon size={18} /> : <DropZoneFileIcon size={18} />}
            </div>

            <div className="fdz-file-info">
              <span className="fdz-file-name" title={file.name}>
                {file.name}
              </span>
              <span className="fdz-file-size">{formatBytes(file.size)}</span>
            </div>

            {isUploading && (
              <div className="fdz-progress-wrap">
                <div className="fdz-progress-bar">
                  <div className="fdz-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <span className="fdz-progress-pct">{progress}%</span>
              </div>
            )}

            {!isUploading && !disabled && (
              <button
                type="button"
                className="fdz-clear-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                aria-label="Remove file"
                title="Remove file"
              >
                <CloseIcon size={11} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Validation error */}
      {error && (
        <p className="fdz-error-msg" role="alert">
          <SmallErrorCircleIcon size={12} />
          {error}
        </p>
      )}

      {/* Upload failed */}
      {isFailed && !error && (
        <p className="fdz-error-msg" role="alert">
          <SmallErrorCircleIcon size={12} />
          Upload failed. Please retry.
        </p>
      )}
    </div>
  );
}

FileDropZone.propTypes = {
  id: PropTypes.string,
  accept: PropTypes.string,
  file: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
  }),
  error: PropTypes.string,
  progress: PropTypes.number,
  onFileSelect: PropTypes.func,
  onClear: PropTypes.func,
  disabled: PropTypes.bool,
  dropLabel: PropTypes.string,
};

export default FileDropZone;
